/**
 * Pure function for computing per-event phase (used by EventSelector for individual event items).
 * Does NOT require hooks; takes all data as arguments.
 */
export function computeEventPhase(
  event: Record<string, unknown>,
  ongoingPayload: Record<string, unknown> | null,
  nowSec: number
): { phase: 'countdown' | 'starting' | 'live' | 'ended' | 'cancelled'; countdownLabel: string | null } {
  const eventId = event?.event_id != null ? String(event.event_id) : null;
  const isOngoing =
    ongoingPayload != null &&
    eventId != null &&
    String(ongoingPayload.event_id ?? '') === eventId;

  if ((event as { cancelled?: boolean }).cancelled === true ||
      (isOngoing && (ongoingPayload as { cancelled?: boolean })?.cancelled === true)) {
    return { phase: 'cancelled', countdownLabel: null };
  }

  if (isOngoing) {
    return { phase: 'live', countdownLabel: null };
  }

  const endTs =
    event?.end_timestamp != null && Number.isFinite(Number(event.end_timestamp))
      ? Number(event.end_timestamp)
      : null;
  if (endTs != null && nowSec >= endTs) {
    return { phase: 'ended', countdownLabel: null };
  }

  const startTs =
    event?.event_timestamp != null && Number.isFinite(Number(event.event_timestamp))
      ? Number(event.event_timestamp)
      : null;

  if (startTs != null && nowSec < startTs) {
    // Minutes rounded up: 1:01 → "2m", 2:36 → "3m"; when < 60s show seconds countdown.
    const secLeft = Math.max(0, Math.floor(startTs - nowSec));
    const m = secLeft >= 60 ? Math.ceil(secLeft / 60) : 0;
    const s = secLeft % 60;
    const countdownLabel = secLeft >= 60 ? `Starts in ${m}m` : `Starts in ${s}s`;
    return { phase: 'countdown', countdownLabel };
  }

  if (startTs != null && (endTs == null || nowSec < endTs)) {
    return { phase: 'starting', countdownLabel: null };
  }

  throw new Error(
    'computeEventPhase: cannot determine phase (event has no valid event_timestamp or timestamps are inconsistent)'
  );
}
